import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
// Highcharts: `ind-trend-chart` renders with Apache ECharts, imported directly
// in that component, rather than the `[chart]` directive.
import { CalendarModule } from '@exeevo/ion2-calendar';
import { VirtualListModule } from '../components/angular-virtual-list';
import { NgxMaterialTimepickerModule } from 'ngx-material-timepicker';
import { TooltipModule } from '@amin-karimi/ng2-tooltip-directive';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { SafePipe } from '../pipes/safe.pipe';
import { OrderByPipe } from '../pipes/orderby.pipe';
import { RepCallPlanStatePipe } from '../pipes/rep-call-plan-state.pipe';
import { ExtendedDatePipe } from '../pipes/extended-date.pipe';
import { ScientificActivityPlanFilter } from '../pipes/scientific-activity-plan.pipe';
import { TransformLinkableText } from '../pipes/common-pipes/transform-linkable-text';
import { FilterPipe } from '../pipes/ngx-filter.pipe';
import { ColorDirective, IonScrollComponent } from '../components/ion-scroll.component';
import { NgOptimizedImage } from '@angular/common';
import { PdfJsViewerModule } from 'ng2-pdfjs-viewer';
import { CapitalizeFirstLetter } from '../pipes/common-pipes/capitalize-first-letter.pipe';
import { HighlightMatchedValuePipe } from '../pipes/common-pipes/highlight-match-key';
import { CoachingItemComponent } from '../components/coaching/coaching-item/coaching-item';
import { MeetingStructure } from '../components/new-components/meeting-structure/meeting-structure';
import { ProductKeyMessagesComponent } from '../components/product-key-messages/product-key-messages';
import { ProductKeyMessagesV2Component } from '../components/activity/product-key-messages-v2/product-key-messages-v2.component';
import { ImagePreloadDirective } from '../directives/image-load';

import { FooterToolbarComponent } from '../components/footer-toolbar/footer-toolbar';
import { EditableNote } from '../components/io-component/io-note/io-note';
import { ExpandableComponent } from '../components/expandable/expandable';
import { SpinnerComponent } from '../components/spinner/spinner';
import { SignaturePad } from '../components/shared/signaturepad/angular2-signaturepad.component';
import { IndPhotoCaptureViewerComponent } from '../components/shared/ind-photo-capture-viewer/ind-photo-capture-viewer';
import { IndPhotoViewerComponent } from '../components/shared/ind-photo-viewer/ind-photo-viewer';
import { DynamicFormLookupPopoverComponent } from '../components/shared/dynamic-form-lookup-popover/dynamic-form-lookup-popover';
import { AlertWithInput } from '../components/shared/alert-with-input/alert-with-input';
import { IndArrowScrollerComponent } from '../components/shared/ind-arrow-scroller/ind-arrow-scroller';
import { IndBlockLevelLoader } from '../components/shared/ind-block-level-loader/ind-block-level-loader';
import { IndChipsComponent } from '../components/shared/ind-chips/ind-chips';
import { DisplayFormComponent } from '../components/shared/ind-display-form/ind-display-form';
import { IndDropdownListComponent } from '../components/shared/ind-dropdown-list/ind-dropdown-list';
import { DynamicFormComponent } from '../components/shared/ind-dynamic-form/ind-dynamic-form';
import { IndFormFieldComponent } from '../components/shared/ind-form-field/ind-form-field';
import { IndHeaderLeftComponent } from '../components/shared/ind-header-left/ind-header-left';
import { IndKeyControlsAreaComponent } from '../components/shared/ind-key-controls-area/ind-key-controls-area';
import { IndListGridComponent } from '../components/shared/ind-list-grid/ind-list-grid';
import { IndPageTitleComponent } from '../components/shared/ind-page-title/ind-page-title';
import { IndPageHeaderComponent } from '../components/shared/ind-page-header/ind-page-header';
import { IndRightpaneReadonlyComponent } from '../components/shared/ind-rightpane-readonly/ind-rightpane-readonly';
import { IndSectionHeaderComponent } from '../components/shared/ind-section-header/ind-section-header';
import { IndTabsComponent } from '../components/shared/ind-tabs/ind-tabs';
import { IndHeaderTabsComponent } from '../components/shared/ind-header-tabs/ind-header-tabs';
import { IndTextAreaComponent } from '../components/shared/ind-text-area/ind-text-area';
import { MainToolTemplateComponent } from '../components/shared/main-tool-template/main-tool-template';
import { NothingSelectedView } from '../components/shared/nothing-selected-view/nothing-selected-view';
import { OmniAccordionComponent } from '../components/shared/omni-accordion/omni-accordion';
import { OmniAccordionItemComponent } from '../components/shared/omni-accordion-item/omni-accordion-item';
import { SearchSuggestionPopoverComponent } from '../components/shared/search-suggestion-popover/search-suggestion-popover';
import { SelectListComponent } from '../components/shared/select-list/select-list';
import { MainCardComponent } from '../components/shared/ui-cards/main-card/main-card';
import { ItemDetailCardComponent } from '../components/shared/ui-cards/item-detail-card/item-detail-card';
import { NumberCardComponent } from '../components/shared/ui-cards/number-card/number-card.component';
import { SubCardComponent } from '../components/shared/ui-cards/sub-card/sub-card';
import { GlanceCardComponent } from '../components/shared/ui-cards/glance-card/glance-card';
import { IndReportGroupTileComponent } from '../components/shared/ind-report-group-tile/ind-report-group-tile';
import { IndReportTileComponent } from '../components/shared/ind-report-tile/ind-report-tile';
import { IndSplitPaneComponent } from '../components/shared/ind-split-pane/ind-split-pane.component';
import { StarRatingComponent } from '../components/shared/star-rating/star-rating.component';
import { BusinessProcessComponent } from '../components/shared/business-process/business-process';
import { IndNoSearchResultComponent } from '../components/shared/ind-no-search-result/ind-no-search-result';
import { IndAffiliationsCardComponent } from '../components/shared/ind-affiliations-card/ind-affiliations-card';
import { IndDatetimeFormComponent } from '../components/shared/ind-datetime-form/ind-datetime-form';
import { SurveyComponent } from '../components/shared/ind-survey/ind-survey';
import { AffiliationExplorerComponent } from '../components/shared/affiliation-explorer/affiliation-explorer';
import { ActivityConflictMsgComponent } from '../components/shared/activity-conflict-alert-msg/activity-conflict-alert-msg';
import { IndHierarchyListItemComponent } from '../components/shared/ind-hierarchy-list-item/ind-hierarchy-list-item';
import { IndFilterMenuComponent } from '../components/shared/ind-filter-menu/ind-filter-menu';
import { IndFilterMenuModalComponent } from '../components/shared/ind-filter-menu-modal/ind-filter-menu-modal';
import { IndAlertMsgComponent } from '../components/shared/ind-alert-msg/ind-alert-msg';
import { IndPopupModalHeaderComponent } from '../components/shared/ind-popup-modal-header/ind-popup-modal-header';
import { AvatarListItemComponent } from '../components/shared/avatar-list-item/avatar-list-item';
import { OmniListItemComponent } from '../components/shared/omni-list-item/omni-list-item.component';
import { SearchFilterComponent } from '../components/shared/search-filter/search-filter';
import { OmniInfoAlertComponent } from '../components/shared/omni-info-alert/omni-info-alert.component';
import { ReorderItemsListComponent } from '../components/shared/reorder-items-list/reorder-items-list-component';
import { IndTextInputPopupComponent } from '../components/shared/ind-text-input-popup/ind-text-input-popup';
import { IndSlideModalComponent } from '../components/shared/ind-slide-modal/ind-slide-modal';
import { OmniAlertComponent } from '../components/shared/omni-alert/omni-alert.component';
import { PersonalizeQuickGlanceComponent } from '../components/shared/personalize-quick-glance/personalize-quick-glance';
import { OmniOptionAlertComponent } from '../components/shared/omni-option-alert/omni-option-alert.component';
import { OmniYearMonthPickerComponent } from '../components/shared/omni-year-month-picker/omni-year-month-picker.component';
import { IndGaugeChartComponent } from '../components/shared/ind-gauge-chart/ind-gauge-chart';
import { IndTrendChartComponent } from '../components/shared/ind-trend-chart/ind-trend-chart';
import { RxTrendsComponent } from '../components/adoption-ladder-journey/rx-trends/rx-trends';
import { ProductPillRowComponent } from '../components/adoption-ladder-journey/product-pill-row/product-pill-row';
import { IndSimpleTooltipComponent } from '../components/shared/ind-simple-tooltip/ind-simple-tooltip';
import { MainToolTemplateDetailsComponent } from '../components/shared/main-tool-template-details/main-tool-template-details';
import { ExpandableTextComponent } from '../components/shared/ind-expandable-text/ind-expandable-text';
import { PinchZoomComponent } from '../components/shared/ind-pinch-zoom/ind-pinch-zoom';
import { IndFormListComponent } from '../components/shared/ind-form-list/ind-form-list.component';
import { SecondaryInfoFormatPipe } from '../pipes/common-pipes/secondary-info-format.pipe';
import { TimelineItemComponent } from '../components/timeline-item/timeline-item.component';
import { DisplayValueComponent } from '../components/display-value/display-value.component';
import { ProgressBarComponent } from '../components/progress-bar/progress-bar';
import { ProgressWithTextComponent } from '../components/progress-with-text/progress-with-text';
import { ListPreviewComponent } from '../components/list-preview/list-preview.component';
import { MultiSelectPopover } from '../components/multi-select-popover/multi-select-popover';
import { PopoverComponent } from '../components/popover/popover';
import { AdoptionLadderJourneyComponent } from '../components/adoption-ladder-journey/adoption-ladder-journey';
import { MoreOptionsPopoverComponent } from '../components/more-options-popover/more-options-popover';
import { SearchLookupPopoverComponent } from '../components/popover/search-lookup-popover/search-lookup-popover';
import { FilterReasonsPipe } from '../pipes/filterReasons.pipe';
import { TextSearchFilterPipe } from '../pipes/common-pipes/text-search.pipe';
import { CallPlanFilterPipe } from '../pipes/call-plan-filter.pipe';
import { IndDateFormatPipe } from '../pipes/common-pipes/date-format.pipe';
import { MonthTranslatePipe } from '../pipes/month-translate/month-translate';
import { DebounceClickDirective } from '../directives/debounce-click-directive';

const SHARED_DECLARATIONS = [
  EditableNote,
  ExpandableComponent,
  SpinnerComponent,
  SignaturePad,
  IndPhotoCaptureViewerComponent,
  IndPhotoViewerComponent,
  DynamicFormLookupPopoverComponent,
  AlertWithInput,
  IndArrowScrollerComponent,
  IndBlockLevelLoader,
  IndChipsComponent,
  IndDropdownListComponent,
  DynamicFormComponent,
  IndKeyControlsAreaComponent,
  IndListGridComponent,
  IndRightpaneReadonlyComponent,
  IndTabsComponent,
  IndHeaderTabsComponent,
  MainToolTemplateComponent,
  SelectListComponent,
  ItemDetailCardComponent,
  NumberCardComponent,
  SubCardComponent,
  GlanceCardComponent,
  IndReportGroupTileComponent,
  IndReportTileComponent,
  IndSplitPaneComponent,
  StarRatingComponent,
  BusinessProcessComponent,
  SurveyComponent,
  AffiliationExplorerComponent,
  ActivityConflictMsgComponent,
  IndHierarchyListItemComponent,
  IndFilterMenuComponent,
  IndFilterMenuModalComponent,
  IndAlertMsgComponent,
  IndPopupModalHeaderComponent,
  AvatarListItemComponent,
  OmniListItemComponent,
  SearchFilterComponent,
  OmniInfoAlertComponent,
  ReorderItemsListComponent,
  IndTextInputPopupComponent,
  IndSlideModalComponent,
  OmniAlertComponent,
  PersonalizeQuickGlanceComponent,
  OmniOptionAlertComponent,
  OmniYearMonthPickerComponent,
  IndGaugeChartComponent,
  IndTrendChartComponent,
  RxTrendsComponent,
  ProductPillRowComponent,
  IndSimpleTooltipComponent,
  MainToolTemplateDetailsComponent,
  PinchZoomComponent,
  SecondaryInfoFormatPipe,
  TimelineItemComponent,
  DisplayValueComponent,
  ProgressBarComponent,
  ProgressWithTextComponent,
  ListPreviewComponent,
  CoachingItemComponent,
  MeetingStructure,
  ProductKeyMessagesComponent,
  ProductKeyMessagesV2Component,
  ImagePreloadDirective,
  RepCallPlanStatePipe,
  ExtendedDatePipe,
  ScientificActivityPlanFilter,
  ColorDirective,
  IonScrollComponent,
  AdoptionLadderJourneyComponent,
  MoreOptionsPopoverComponent,
  SearchLookupPopoverComponent,
  TextSearchFilterPipe,
  CallPlanFilterPipe,
  IndDateFormatPipe,
  MonthTranslatePipe,
  DebounceClickDirective,
];

@NgModule({
  imports: [
    ExpandableTextComponent,
    CommonModule,
    IonicModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    CalendarModule,
    VirtualListModule,
    NgxMaterialTimepickerModule,
    TooltipModule,
    DragDropModule,
    SafePipe,
    NgOptimizedImage,
    PdfJsViewerModule,
    CapitalizeFirstLetter,
    OrderByPipe,
    FilterPipe,
    IndPageTitleComponent,
    IndPageHeaderComponent,
    IndSectionHeaderComponent,
    NothingSelectedView,
    IndNoSearchResultComponent,
    FooterToolbarComponent,
    IndHeaderLeftComponent,
    MultiSelectPopover,
    PopoverComponent,
    FilterReasonsPipe,
    MainCardComponent,
    HighlightMatchedValuePipe,
    SearchSuggestionPopoverComponent,
    DisplayFormComponent,
    IndFormFieldComponent,
    IndTextAreaComponent,
    OmniAccordionComponent,
    OmniAccordionItemComponent,
    IndAffiliationsCardComponent,
    TransformLinkableText,
    IndFormListComponent,
    IndDatetimeFormComponent,
  ],
  declarations: SHARED_DECLARATIONS,
  exports: [
    ...SHARED_DECLARATIONS,
    ExpandableTextComponent,
    // Re-export core Angular modules so importing modules don't need to import them separately
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    IonicModule,
    CalendarModule,
    VirtualListModule,
    TooltipModule,
    DragDropModule,
    SafePipe,
    NgOptimizedImage,
    PdfJsViewerModule,
    CapitalizeFirstLetter,
    OrderByPipe,
    FilterPipe,
    IndPageTitleComponent,
    IndPageHeaderComponent,
    IndSectionHeaderComponent,
    NothingSelectedView,
    IndNoSearchResultComponent,
    FooterToolbarComponent,
    IndHeaderLeftComponent,
    MultiSelectPopover,
    PopoverComponent,
    FilterReasonsPipe,
    MainCardComponent,
    HighlightMatchedValuePipe,
    SearchSuggestionPopoverComponent,
    DisplayFormComponent,
    IndFormFieldComponent,
    IndTextAreaComponent,
    OmniAccordionComponent,
    OmniAccordionItemComponent,
    IndAffiliationsCardComponent,
    TransformLinkableText,
    IndFormListComponent,
    IndDatetimeFormComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class SharedModule {}
